/**
 * Minimal HTTP-fetch coordinator: request deduplication + staleness tracking +
 * retry. Replaces TanStack Query's fetch role for collection-backed data — the
 * fetched data lands in a Collection (the cache), so this only coordinates the
 * *fetching*, not storage.
 */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
export class Fetcher {
    /** Is the key currently fresh (within its staleMs window)? */ isFresh(key) {
        const until = this.freshUntil.get(key);
        return until !== undefined && Date.now() < until;
    }
    /** Is a fetch for this key currently in flight? */ isFetching(key) {
        return this.pending.has(key);
    }
    /**
   * Fetch for `key`, deduping concurrent calls. Skips the call entirely when
   * the key is still fresh (returns the in-flight promise if one exists, else
   * resolves to `undefined` to signal "use cached"). Use `force` to bypass
   * freshness.
   */ fetch(key, fn, opts = {}, force = false) {
        var _opts_retries, _opts_retryBackoffMs;
        const existing = this.pending.get(key);
        if (existing) {
            return existing;
        }
        if (!force && this.isFresh(key)) {
            return Promise.resolve(undefined);
        }
        const run = this.withRetry(fn, (_opts_retries = opts.retries) !== null && _opts_retries !== void 0 ? _opts_retries : 0, (_opts_retryBackoffMs = opts.retryBackoffMs) !== null && _opts_retryBackoffMs !== void 0 ? _opts_retryBackoffMs : 300);
        const promise = run.then((result)=>{
            var _opts_staleMs;
            this.freshUntil.set(key, Date.now() + ((_opts_staleMs = opts.staleMs) !== null && _opts_staleMs !== void 0 ? _opts_staleMs : 0));
            return result;
        }).finally(()=>{
            this.pending.delete(key);
        });
        this.pending.set(key, promise);
        return promise;
    }
    /** Mark a key fresh without fetching (e.g. after an external push). */ markFresh(key, staleMs = 0) {
        this.freshUntil.set(key, Date.now() + staleMs);
    }
    /** Invalidate a key so the next fetch runs. */ invalidate(key) {
        this.freshUntil.delete(key);
    }
    async withRetry(fn, retries, backoffMs) {
        let attempt = 0;
        // eslint-disable-next-line no-constant-condition
        while(true){
            try {
                return await fn();
            } catch (error) {
                if (attempt >= retries) {
                    throw error;
                }
                const wait = backoffMs * 2 ** attempt;
                attempt += 1;
                await new Promise((resolve)=>{
                    setTimeout(resolve, wait);
                });
            }
        }
    }
    constructor(){
        /** In-flight fetches by key — concurrent callers share one promise. */ _define_property(this, "pending", new Map());
        /** Epoch-ms until which a key's data is considered fresh. */ _define_property(this, "freshUntil", new Map());
    }
}
/** Shared default fetcher instance. */ export const fetcher = new Fetcher();
